import { useEffect, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { IdControl } from './IdControl'
import { NamesControl } from './NamesControl'
import { PersonalDataControl } from './PersonalDataControl'
import {
    CONDITIONAL_DEPENDENCIES,
    FIELD_DEPENDENCIES,
    getAge,
    validateCity
} from './functionsValidation'

import { useProviderLoader } from '@/Providers/useProviderLoader'
import { IsAgentComponent } from '@/components/common'
import { CheckboxControl, FormNavigation } from '@/components/form-controls'
import { useToast } from '@/context/ToastContext'
import mockDropdowns from '@/data/forms/mockDropdowns.json'
import { useAuthHook } from '@/hooks/useAuthHook'
import { useAspirantsApi } from '@/services/api/aspirantsApi'
import { useAuthStore } from '@/store/authStore'
import { ErrorHandlingStrategy, MessageLevel } from '@/types/api'
import { Authorization, AuthorizationType } from '@/types/authorizationForm'
import { BasicInfoFormData } from '@/types/forms/basicInfoForm'
import { FormsGroupsProps } from '@/types/forms/formStatus'
import { OptionsField } from '@/types/forms/optionsField'
import { useFormProgress } from '@/utils'
import { DateParser } from '@/utils/dateParser'
import { DocumentTypeCodes } from '@/utils/documentValidationUtils'

// eslint-disable-next-line max-lines-per-function
export const BasicInfoForm = ({
    onNext,
    onPrevious,
    isReadOnly = false,
    onBlockedFieldClick
}: FormsGroupsProps): React.ReactElement => {
    const { t } = useTranslation()
    const [isFormTouched, setIsFormTouched] = useState(false)
    const { control, handleSubmit, reset, watch, setValue, trigger, resetField } =
        useForm<BasicInfoFormData>({
            mode: 'onTouched', // Valida cuando el usuario sale del campo (blur) o interactúa con él
            reValidateMode: 'onChange', // Una vez hay un error, lo limpia apenas se corrija
            defaultValues: {
                // Asegurar valores por defecto para evitar validaciones prematuras
                birthDate: '',
                documentExpeditionDate: '',
                idDocumentType: ''
            }
        })
    const { isAgent, idAsesor, updateIsMinor } = useAuthStore()
    const { idAspirant, idInscription } = useAuthHook()
    const updateUserData = useAuthStore((state) => state.updateUserData)
    const aspirantsApi = useAspirantsApi({
        messageLevel: MessageLevel.ERRORS,
        errorHandling: ErrorHandlingStrategy.RETURN
    })
    const { showLoader, hideLoader } = useProviderLoader()
    const [disable, setDisable] = useState<boolean>(false)
    const progress = useFormProgress<BasicInfoFormData>({
        watch,
        excludedFields: [
            'id',
            'isActive',
            'createdAt',
            'updatedAt',
            'habeasData',
            'middleName',
            'secondLastName',
            'isMinor',
            'numberOfChildren',
            'numberChildren'
        ],
        conditionalFields: [
            {
                field: 'expeditionDocumentPlace',
                dependsOn: 'countryExpedition',
                condition: 'CO'
            }
        ]
    })
    const { showToast } = useToast()

    const text = 'basicDataInformation'

    const loadBasicData = async (): Promise<void> => {
        showLoader()

        const { data, status } = await aspirantsApi.getBasicData(idAspirant!)
        const responseAuthByInscription =
            await aspirantsApi.getAuthorizationAspirantByInscription(
                idAspirant!,
                idInscription!
            )

        console.log('isMinorResponse', responseAuthByInscription)
        hideLoader()

        if (status === 200) {
            const isMinorConfirmed = responseAuthByInscription.data.some(
                (auth: Authorization) =>
                    auth.authorizationType === AuthorizationType.IS_MINOR &&
                    auth.isAccepted
            )

            console.log('idInscription', idInscription)

            // isMinor se deriva del tipo de documento (TI siempre es menor, CC nunca)
            // y, para los demás, de la fecha de nacimiento: el valor guardado puede
            // estar desactualizado (cambio de tipo de documento, extranjeros)
            const parsedBirthDate = data.birthDate
                ? DateParser.parseDate(String(data.birthDate))
                : null
            const responseFormatted = {
                ...data,
                haveChildren: data.haveChildren?.toString(),
                isMinor:
                    data.idDocumentType ===
                        DocumentTypeCodes.TARJETA_IDENTIDAD ||
                    (data.idDocumentType !==
                        DocumentTypeCodes.CEDULA_CIUDADANIA &&
                        (parsedBirthDate
                            ? getAge(parsedBirthDate) < 18
                            : data.isMinor)),
                isMinorConfirmed: isMinorConfirmed
            }

            console.log('responseFormatted', responseFormatted)

            updateIsMinor(responseFormatted.isMinor ?? null)
            setDisable(isMinorConfirmed === true)
            reset(responseFormatted)
        }
    }

    useEffect(() => {
        loadBasicData()
    }, [idAspirant, reset])

    // Detectar cuando el usuario interactúa con el formulario
    const watchedValues = watch()

    useEffect(() => {
        // Marcar como tocado si hay algún valor
        const hasValues = Object.values(watchedValues).some(
            (value) =>
                value && value !== '' && value !== null && value !== undefined
        )

        if (hasValues && !isFormTouched) {
            setIsFormTouched(true)
        }
    }, [watchedValues, isFormTouched])

    const handleSubmissionSuccess = () => {
        hideLoader()

        // Actualizar el estado de los formularios antes de continuar
        if (idAspirant) {
            aspirantsApi
                .getStatusForms(idAspirant, idInscription || 0)
                .then(() =>
                    console.log(
                        'Form status updated after BasicInfo completion'
                    )
                )
                .catch((error) =>
                    console.error('Error updating form status:', error)
                )
        }

        onNext()
    }

    const handleAuthorizationError = (minorResponse: any) => {
        const errorMessage =
            typeof minorResponse.message === 'string'
                ? minorResponse.message
                : Array.isArray(minorResponse.message)
                    ? (minorResponse.message[0] as any)?.messageDesc || ''
                    : (minorResponse.message as any)?.messageDesc || ''

        if (
            errorMessage.includes('existe') ||
            errorMessage.includes('ya existe')
        ) {
            showToast({
                title: 'Información',
                message: 'La autorización ya fue registrada anteriormente',
                type: 'info'
            })
            handleSubmissionSuccess()
        } else {
            hideLoader()
            showToast({
                title: 'Error',
                message: 'Error al guardar la autorización',
                type: 'error'
            })
        }
    }

    const handleCatchError = (error: any) => {
        hideLoader()
        const errorMessage = error.message || ''

        if (
            errorMessage.includes('Duplicate entry') ||
            errorMessage.includes('unique_aspirant_auth')
        ) {
            showToast({
                title: 'Información',
                message: 'La autorización ya fue registrada anteriormente',
                type: 'info'
            })
            onNext()
        } else {
            showToast({
                title: 'Error',
                message:
                    'Error al guardar la autorización, para cambiar la autorización puedes comunicarte en atencion virtual al area de admisiones en meli',
                type: 'error'
            })
        }
    }

    // Función para limpiar campos dependientes cuando el usuario hace cambios manuales
    const handleFieldChange = (field: string, newValue: any) => {
        // Primero establecer el valor del campo que cambió
        setValue(field as keyof BasicInfoFormData, newValue)

        const parentField = field as keyof typeof FIELD_DEPENDENCIES
        const dependentFields = FIELD_DEPENDENCIES[parentField]

        if (dependentFields) {
            dependentFields.forEach((dependentField) => {
                // Usar resetField en lugar de setValue para limpiar correctamente
                resetField(dependentField as keyof BasicInfoFormData, {
                    defaultValue: ''
                })
            })
        }

        // Verificar dependencias condicionales
        const conditionalDep =
            CONDITIONAL_DEPENDENCIES[
            parentField as keyof typeof CONDITIONAL_DEPENDENCIES
            ]

        if (conditionalDep && newValue !== conditionalDep.condition) {
            resetField(conditionalDep.field as keyof BasicInfoFormData, {
                defaultValue: ''
            })
        }
    }

    const onSubmit: SubmitHandler<BasicInfoFormData> = async (
        data: BasicInfoFormData
    ) => {
        try {
            if (!idAspirant) {
                return
            }
            console.log('data', data)
            showLoader()

            const payload = validateCity({
                ...data
            })

            const [basicResponse, minorResponse] = await Promise.all([
                aspirantsApi.postBasicData(idAspirant, payload, idAsesor || 0),
                aspirantsApi.postAuthorizationAspiranByScope({
                    idAspirant: idAspirant!,
                    idInscription: idInscription || undefined,
                    updatedBy: idAspirant!,
                    authorizations: [
                        {
                            authorizationTypeId: AuthorizationType.IS_MINOR,
                            isAccepted: data.isMinorConfirmed,
                            valueText: ''
                        }
                    ]
                })
            ])

            if (basicResponse.status === 200 || basicResponse.status === 201) {
                if (
                    minorResponse.status === 200 ||
                    minorResponse.status === 201
                ) {
                    handleSubmissionSuccess()
                } else {
                    handleAuthorizationError(minorResponse)
                }
            } else {
                hideLoader()
                showToast({
                    title: 'Error',
                    message: 'Error al guardar los datos básicos',
                    type: 'error'
                })
            }
        } catch (error: any) {
            handleCatchError(error)
        }
    }

    return (
        <div className={`relative ${isReadOnly ? 'opacity-60' : ''}`}>
            {isReadOnly && (
                <>
                    <div className="absolute inset-0 z-10 bg-gray-200 bg-opacity-50 cursor-not-allowed" />
                    <div
                        className="absolute inset-0 z-20 bg-transparent cursor-pointer"
                        onClick={onBlockedFieldClick}
                    />
                    <div className="absolute top-2 right-2 z-30 bg-yellow-100 border border-yellow-400 text-yellow-800 px-2 py-1 rounded text-xs">
                        🔒 Campos bloqueados - Click para más información
                    </div>
                </>
            )}
            <form
                onSubmit={handleSubmit(onSubmit)}
                className="w-full space-y-4"
            >
                <IdControl
                    control={control}
                    watchCountryExpedition={watch('countryExpedition')}
                    watchDepartmentExpedition={watch('departmentExpedition')}
                    handleFieldChange={handleFieldChange}
                    watchDocumentType={watch('idDocumentType')}
                    watchBirthDate={watch('birthDate')}
                    setIsMinor={(isMinor) => {
                        setValue('isMinor', isMinor)
                    }}
                    trigger={
                        trigger as (
                            name: keyof BasicInfoFormData
                        ) => Promise<boolean>
                    }
                    text={text}
                    disableIdFields={true}
                    isFormTouched={isFormTouched}
                />
                {watch('isMinor') && (
                    <div className="py-2">
                        <IsAgentComponent isAgent={isAgent}>
                            <CheckboxControl
                                name="isMinorConfirmed"
                                control={control}
                                label={t('authorization.isMinor.acceptTerms')}
                                rules={{
                                    required: t(
                                        'authorization.isMinor.required'
                                    )
                                }}
                                classNameLabel="text-sm"
                                isDisabled={disable || isAgent}
                            />
                        </IsAgentComponent>
                    </div>
                )}
                <NamesControl control={control} />
                <PersonalDataControl
                    control={control}
                    optionsBoolean={
                        mockDropdowns.optionsBoolean as unknown as OptionsField[]
                    }
                    text={text}
                    watch={watch}
                    setValue={setValue}
                />

                <FormNavigation
                    onSubmit={handleSubmit(onSubmit)}
                    onPrevious={onPrevious}
                    progress={progress}
                    previousLabel={t('forms.ui.previous')}
                    submitLabel={t('forms.ui.nextPage')}
                    triggerValidation={trigger}
                />
            </form>
        </div>
    )
}
